import { CourseGeneratorFormValues, CourseContent } from '../types';

export const generateCoursePrompt = (values: CourseGeneratorFormValues): string => {
  return `You are a world class educational content creator.
Generate a comprehensive learning module for the following course:
Topic: ${values.topicName}
Additional Context: ${values.additionalContext}

Please provide a detailed learning module that delivers a complete educational experience.

IMPORTANT FORMATTING INSTRUCTIONS:
1. Respond ONLY with a valid JSON object
2. Do not include any explanatory text before or after the JSON
3. Ensure all strings are properly escaped
4. Each field must exactly match the format shown below
5. Do not add any additional fields
6. Make sure all brackets and braces are properly closed

SEARCH OPTIMIZATION INSTRUCTIONS:
1. Include specific, searchable keywords and phrases in all content sections
2. Add technical terms, tool names, methodologies, and industry-standard terminology
3. Include exact names of relevant frameworks, libraries, or technologies
4. For code-related topics, include programming languages, popular libraries, and framework names
5. Use fully qualified terms rather than acronyms where appropriate
6. Include names of recommended books, courses, or resources
7. Structure key concepts as discrete, copy-pastable phrases

The response must be a JSON object with exactly this structure:
{
  "content": "Module Learning Plan:\\n\\nKey Learning Objectives:\\n[3-4 main objectives for the module with specific, searchable keywords]\\n\\nCore Concepts:\\n[List of fundamental concepts to be covered with precise technical terminology]\\n\\nLearning Resources:\\n[Key resources, materials, and tools needed with exact names and versions]\\n\\nSkills Development:\\n[Specific skills to be developed with industry-standard terminology]\\n\\nPractical Applications:\\n[How the concepts will be applied with specific use cases]\\n\\nSuccess Criteria:\\n[What learners should be able to demonstrate by the module's end with measurable outcomes]",
  "faqs": [
    {
      "question": "What prior knowledge is needed for this module's content?",
      "answer": "Concise but complete answer focusing on prerequisites, with specific technologies or concepts that can be searched for tutorials"
    },
    {
      "question": "How should I prepare my learning environment?",
      "answer": "Practical setup and preparation guidance with exact software names, versions, and configuration requirements"
    },
    {
      "question": "What are the most important concepts to grasp in this module?",
      "answer": "Key concepts and fundamentals to focus on, with specific terminology that can be used for further research"
    },
    {
      "question": "How can I maintain steady progress?",
      "answer": "Progress tracking and consistency tips with names of specific methodologies or tools"
    },
    {
      "question": "What should I do if I need to adjust my learning pace?",
      "answer": "Adaptation strategies and flexibility advice with specific resource recommendations"
    }
  ],
  "learningHurdles": [
    "Learning Blindspot 1: [Common misconception or knowledge gap with precise terminology and explanation of why it's misleading]",
    "Learning Blindspot 2: [Frequently confused concept with clarification and specific examples to distinguish it]",
    "Learning Blindspot 3: [Counter-intuitive aspect of the topic with explanation of the correct mental model]",
    "Learning Blindspot 4: [Common implementation mistake with specific error patterns and how to recognize them]",
    "Learning Blindspot 5: [Outdated or deprecated approach often found in online resources with current best practices]"
  ],
  "realWorldActions": [
    "Practical Exercise 1: [Hands-on application task with specific tools, libraries, or frameworks to use]",
    "Practical Exercise 2: [Real-world implementation with industry-standard approaches and methodologies]",
    "Practical Exercise 3: [Project-based learning activity with specific deliverables and technologies]"
  ],
  "reflectionPrompts": [
    "Understanding Check: [Reflection on core concepts with technical terminology]",
    "Application Analysis: [Reflection on practical implementation with specific examples]",
    "Progress Evaluation: [Self-assessment of learning journey with key skills to evaluate]"
  ],
  "assignment": "Module Project: [Comprehensive assignment that demonstrates mastery of key concepts and practical skills, with specific requirements and technologies to implement]"
}

Content Guidelines:
1. Focus on clear learning objectives with measurable outcomes
2. Emphasize practical applications using industry-standard approaches
3. Include specific technical terminology and exact names (frameworks, libraries, tools)
4. Use proper nouns for technologies, methodologies, and resources
5. Include exact version numbers where relevant
6. Structure content with discrete, searchable phrases
7. Balance theory and practice with searchable examples
8. Provide clear success criteria with specific indicators
9. For Learning Blindspots, focus on identifying and correcting common misconceptions, outdated practices, frequently confused concepts, and counter-intuitive aspects of the topic
10. Ensure Learning Blindspots help students recognize and avoid cognitive biases and mistakes frequently made by beginners`;
}; 